export const MAX_ANALYTICS_DATE_RANGE_DAYS = 30;
export const ANALYTICS_CHUNK_DAYS = 3;

const parseLocalDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getInclusiveDateRangeDays = (startDate, endDate) => {
  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);
  if (!start || !end) return 0;

  const startMidnight = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endMidnight = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.floor((endMidnight - startMidnight) / (1000 * 60 * 60 * 24)) + 1;
};

export const validateAnalyticsDateRange = (
  dateOption,
  customDateRange,
  t = (value) => value,
) => {
  const translate = typeof t === "function" ? t : (val) => val;
  if (dateOption !== "custom") return "";

  const { startDate, endDate } = customDateRange || {};
  if (!startDate || !endDate) {
    return translate("Please select both start and end dates");
  }

  const start = parseLocalDate(startDate);
  const end = parseLocalDate(endDate);

  if (!start || !end) {
    return translate("Please select a valid date range");
  }

  if (start > end) {
    return translate("Start date must be before or equal to end date");
  }

  if (
    getInclusiveDateRangeDays(startDate, endDate) >
    MAX_ANALYTICS_DATE_RANGE_DAYS
  ) {
    return translate("Date range cannot exceed 30 days");
  }

  return "";
};

// Resolves a dateOption ("today" | "yesterday" | "last 7 days" | "last 14 days" | "last 30 days" | "this week" | "this month" | "custom")
// into a concrete { start, end } pair (Date objects, local midnight),
// so it can be run through the same chunking logic.
const resolveConcreteDateRange = (dateOption, customDateRange) => {
  const normalizedOption = String(dateOption || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (normalizedOption === "today") {
    return { start: today, end: today };
  }

  if (normalizedOption === "yesterday") {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return { start: yesterday, end: yesterday };
  }

  if (normalizedOption === "last 7 days") {
    const start = new Date(today);
    start.setDate(start.getDate() - 6);
    return { start, end: today };
  }

  if (normalizedOption === "last 14 days") {
    const start = new Date(today);
    start.setDate(start.getDate() - 13);
    return { start, end: today };
  }

  if (normalizedOption === "last 30 days") {
    const start = new Date(today);
    start.setDate(start.getDate() - 29);
    return { start, end: today };
  }

  if (normalizedOption === "this week") {
    const weekday = today.getDay();
    const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
    const start = new Date(today);
    start.setDate(today.getDate() + mondayOffset);
    return { start, end: today };
  }

  if (normalizedOption === "this month") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { start, end: today };
  }

  if (normalizedOption === "custom") {
    const start = parseLocalDate(customDateRange?.startDate || customDateRange?.start);
    const end = parseLocalDate(customDateRange?.endDate || customDateRange?.end);
    if (!start || !end || start > end) return null;
    return { start, end };
  }

  return null;
};

export const createAnalyticsDateChunks = (dateOption, customDateRange) => {
  const range = resolveConcreteDateRange(dateOption, customDateRange);
  if (!range) return [];

  const { start, end } = range;
  const totalDays = getInclusiveDateRangeDays(formatLocalDate(start), formatLocalDate(end));

  // Ranges that are already <= ANALYTICS_CHUNK_DAYS gain nothing from chunking
  // (today, yesterday, and any range <= 3 days) — one request stays fastest.
  const normalized = String(dateOption || "").trim().toLowerCase().replace(/_/g, " ");
  if (totalDays <= ANALYTICS_CHUNK_DAYS) {
    const isNativeOption = normalized === "today" || normalized === "yesterday";
    return [
      {
        dateOption: isNativeOption ? normalized : "custom",
        customStartDate: formatLocalDate(start),
        customEndDate: formatLocalDate(end),
      },
    ];
  }

  const chunks = [];
  let currentStart = new Date(start);

  while (currentStart <= end) {
    const currentEnd = new Date(currentStart);
    currentEnd.setDate(currentEnd.getDate() + ANALYTICS_CHUNK_DAYS - 1);
    if (currentEnd > end) {
      currentEnd.setTime(end.getTime());
    }

    chunks.push({
      dateOption: "custom",
      customStartDate: formatLocalDate(currentStart),
      customEndDate: formatLocalDate(currentEnd),
    });

    currentStart = new Date(currentEnd);
    currentStart.setDate(currentStart.getDate() + 1);
  }

  return chunks;
};

export const mergeReportResponses = (responses) => {
  if (!Array.isArray(responses) || !responses.length) return null;
  const validResponses = responses.filter(Boolean);
  if (!validResponses.length) return null;

  const data = validResponses.flatMap((response) =>
    Array.isArray(response?.data) ? response.data : [],
  );
  const devices = validResponses.flatMap((response) =>
    Array.isArray(response?.devices) ? response.devices : [],
  );
  const stops = validResponses.flatMap((response) =>
    Array.isArray(response?.stops) ? response.stops : [],
  );
  const halts = validResponses.flatMap((response) =>
    Array.isArray(response?.halts) ? response.halts : [],
  );
  const visits = validResponses.flatMap((response) =>
    Array.isArray(response?.visits) ? response.visits : [],
  );
  const rows = validResponses.flatMap((response) =>
    Array.isArray(response?.rows) ? response.rows : [],
  );

  return {
    ...(validResponses[validResponses.length - 1] || {}),
    data,
    ...(devices.length ? { devices } : {}),
    ...(stops.length ? { stops } : {}),
    ...(halts.length ? { halts } : {}),
    ...(visits.length ? { visits } : {}),
    ...(rows.length ? { rows } : {}),
  };
};